/* Phase 6.5 / 6.6 - <filter-pills> Custom Element.
 *
 * Wraps a collection-listing section. Intercepts pill clicks + sort
 * dropdown changes, fetches the filtered section via the Section
 * Rendering API, and swaps the rendered HTML in place. Updates browser
 * URL via history.pushState. Supports back/forward navigation via
 * popstate.
 *
 * Phase 6.5 baseline:
 *   - Click pill -> preventDefault -> fetch <href>?section_id=<id> -> swap.
 *   - popstate (back/forward) -> fetch new URL's section -> swap.
 *   - No JS -> full-reload fallback (pills are real anchors, sort form
 *     submits via noscript button).
 *
 * Phase 6.6 latency masking (Tier 1):
 *   - Optimistic active-pill state: on click, flip the clicked pill's
 *     `--active` + aria-pressed immediately, before the fetch.
 *   - Skeleton overlay + fade: add `is-loading` class on the section
 *     during fetch (handled in collection.css).
 *   - View Transitions API: wrap the DOM swap in
 *     document.startViewTransition() when supported.
 *
 * Sort dropdown (added later):
 *   - change on select[name="sort_by"] -> build URL preserving existing
 *     query params (active filter) + new sort_by -> _swap. Same SRA path
 *     as pills, gets the same skeleton + crossfade.
 *
 * Dispatched events: collection:filter-changed (detail: { url, productCount })
 * Consumed events: none.
 */
class FilterPills extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onPopstate = this._onPopstate.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    window.addEventListener('popstate', this._onPopstate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
    window.removeEventListener('popstate', this._onPopstate);
  }

  _onClick(event) {
    const pill = event.target.closest('a.collection-listing__pill');
    if (!pill) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const href = pill.getAttribute('href');
    if (!href) return;
    this._optimisticActivate(pill);
    this._swap(href).then(() => {
      history.pushState(null, '', href);
    });
  }

  _onChange(event) {
    const select = event.target.closest('select[name="sort_by"]');
    if (!select) return;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', select.value);
    const href = window.location.pathname + '?' + params.toString();
    this._swap(href).then(() => {
      history.pushState(null, '', href);
    });
  }

  _onPopstate() {
    this._swap(window.location.pathname + window.location.search);
  }

  /* Tier 1 #1 - flip active state on the clicked pill BEFORE the fetch
   * completes so the pill responds in one frame instead of one second.
   * The eventual section swap overwrites this state with the
   * server-rendered truth, which will match in the happy path. */
  _optimisticActivate(clickedPill) {
    const pills = this.querySelectorAll('a.collection-listing__pill');
    pills.forEach((p) => {
      const isClicked = p === clickedPill;
      p.classList.toggle('collection-listing__pill--active', isClicked);
      if (isClicked) {
        p.setAttribute('aria-current', 'page');
      } else {
        p.removeAttribute('aria-current');
      }
    });
  }

  async _swap(href) {
    if (!this.sectionId) return;
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);

    /* Tier 1 #2 - mark loading so collection.css fades the grid and shows
     * a skeleton during the fetch. */
    const section = this.querySelector('.collection-listing');
    if (section) section.classList.add('is-loading');

    try {
      const res = await fetch(url.toString(), { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('.collection-listing');
      const oldSection = this.querySelector('.collection-listing');
      if (!newSection || !oldSection) return;

      /* Tier 1 #3 - wrap the DOM swap in View Transitions API when
       * supported for a native browser crossfade between old/new state.
       * Falls through to a raw replaceWith() in unsupported browsers. */
      const performSwap = () => oldSection.replaceWith(newSection);
      if (document.startViewTransition) {
        await document.startViewTransition(performSwap).finished;
      } else {
        performSwap();
      }

      const countEl = newSection.querySelector('.collection-listing__count');
      const productCount = countEl ? countEl.textContent.trim() : null;
      this.dispatchEvent(new CustomEvent('collection:filter-changed', {
        bubbles: true,
        detail: { url: href, productCount }
      }));
    } catch (err) {
      console.error('[filter-pills]', 'swap-failed', err);
      window.location.href = href;
    }
  }
}

customElements.define('filter-pills', FilterPills);
